import * as React from 'react';

/*
 * PopoverSelect
 * -------------
 * Generic floating-popover select with title + optional description per option.
 * Replaces native <select> when you want richer per-option labels (e.g. role
 * descriptions, member names + emails). Positions itself with `position: fixed`
 * anchored to the trigger's bounding rect, so the menu escapes any clipping
 * ancestor (modal panels with overflow: auto, etc.) and auto-flips above the
 * trigger when there's not enough room below.
 *
 * Usage:
 *   <PopoverSelect
 *     value={roleId}
 *     options={[
 *       { value: 1, title: 'Admin', description: 'Manages everything…' },
 *       { value: 2, title: 'Recruiter', description: 'Posts and manages jobs.' }
 *     ]}
 *     onChange={(next) => setRoleId(next)}
 *     ariaLabel="Role"
 *   />
 */

export interface PopoverSelectOption<T extends string | number> {
  value: T;
  title: string;
  description?: string | null;
}

interface MenuPosition {
  top: number;
  left: number;
  minWidth: number;
}

export interface PopoverSelectProps<T extends string | number> {
  value: T | null | undefined;
  options: PopoverSelectOption<T>[];
  onChange: (value: T) => void;
  disabled?: boolean;
  ariaLabel?: string;
  placeholder?: string;
  /** Override the trigger label (defaults to selected option's title). */
  renderTriggerLabel?: (selected: PopoverSelectOption<T> | undefined) => React.ReactNode;
  /** Minimum menu width in pixels. Defaults to 280. */
  menuMinWidth?: number;
}

const DEFAULT_MIN_WIDTH = 280;
const ESTIMATED_OPTION_HEIGHT = 70;

const PopoverSelectInner = <T extends string | number>({
  value,
  options,
  onChange,
  disabled = false,
  ariaLabel = 'Select an option',
  placeholder = 'Select…',
  renderTriggerLabel,
  menuMinWidth = DEFAULT_MIN_WIDTH
}: PopoverSelectProps<T>): React.ReactElement => {
  const [open, setOpen] = React.useState(false);
  const [menuPosition, setMenuPosition] = React.useState<MenuPosition | null>(null);
  const [activeIndex, setActiveIndex] = React.useState<number>(-1);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);
  const menuRef = React.useRef<HTMLUListElement | null>(null);
  const optionRefs = React.useRef<(HTMLLIElement | null)[]>([]);

  const selected = options.find((option) => option.value === value);
  const selectedIndex = options.findIndex((option) => option.value === value);

  React.useEffect(() => {
    if (!open) return;
    const initial = selectedIndex >= 0 ? selectedIndex : 0;
    setActiveIndex(initial);
    // Focus the active option after render so screen readers announce it
    // and arrow-key navigation has a starting point.
    const t = window.setTimeout(() => {
      optionRefs.current[initial]?.focus();
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, selectedIndex]);

  const handleListKey = (event: React.KeyboardEvent<HTMLUListElement>) => {
    if (options.length === 0) return;
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setActiveIndex((prev) => {
        const next = prev < options.length - 1 ? prev + 1 : 0;
        optionRefs.current[next]?.focus();
        return next;
      });
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((prev) => {
        const next = prev > 0 ? prev - 1 : options.length - 1;
        optionRefs.current[next]?.focus();
        return next;
      });
    } else if (event.key === 'Home') {
      event.preventDefault();
      setActiveIndex(0);
      optionRefs.current[0]?.focus();
    } else if (event.key === 'End') {
      event.preventDefault();
      setActiveIndex(options.length - 1);
      optionRefs.current[options.length - 1]?.focus();
    } else if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (activeIndex >= 0 && activeIndex < options.length) {
        onChange(options[activeIndex].value);
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
  };

  const handleTriggerKey = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!open) {
        const position = computePosition();
        if (position) setMenuPosition(position);
        setOpen(true);
      }
    }
  };

  const computePosition = React.useCallback((): MenuPosition | null => {
    if (!triggerRef.current) return null;
    const rect = triggerRef.current.getBoundingClientRect();
    const estimatedMenuHeight = Math.min(options.length || 1, 6) * ESTIMATED_OPTION_HEIGHT + 16;
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropUp = spaceBelow < estimatedMenuHeight && rect.top > spaceBelow;
    const minWidth = Math.max(rect.width, menuMinWidth);
    const left = Math.min(rect.right - minWidth, window.innerWidth - minWidth - 8);
    return {
      top: dropUp ? rect.top - 6 - estimatedMenuHeight : rect.bottom + 6,
      left: Math.max(8, left),
      minWidth
    };
  }, [options.length, menuMinWidth]);

  React.useEffect(() => {
    if (!open) return undefined;
    const handleClick = (event: MouseEvent) => {
      const target = event.target as Node;
      if (containerRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const handleScrollOrResize = () => {
      const next = computePosition();
      if (next) setMenuPosition(next);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [open, computePosition]);

  const handleToggle = () => {
    if (disabled) return;
    if (!open) {
      const position = computePosition();
      if (position) setMenuPosition(position);
    }
    setOpen((prev) => !prev);
  };

  const triggerLabel = renderTriggerLabel
    ? renderTriggerLabel(selected)
    : selected?.title || placeholder;

  return (
    <div className="popover-select org-wizard-role-select" ref={containerRef}>
      <button
        type="button"
        ref={triggerRef}
        className="popover-select__trigger org-wizard-role-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={handleToggle}
        onKeyDown={handleTriggerKey}
      >
        <span className="popover-select__value org-wizard-role-select__value">{triggerLabel}</span>
        <i className="fa fa-chevron-down" aria-hidden="true" />
      </button>
      {open && menuPosition ? (
        <ul
          ref={menuRef}
          className="popover-select__menu popover-select__menu--floating org-wizard-role-select__menu org-wizard-role-select__menu--floating"
          role="listbox"
          aria-label={ariaLabel}
          aria-activedescendant={
            activeIndex >= 0 && activeIndex < options.length
              ? `popover-option-${String(options[activeIndex].value)}`
              : undefined
          }
          onKeyDown={handleListKey}
          style={{
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            minWidth: `${menuPosition.minWidth}px`
          }}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value;
            const isActive = index === activeIndex;
            return (
              <li
                key={String(option.value)}
                id={`popover-option-${String(option.value)}`}
                role="option"
                aria-selected={isSelected}
                tabIndex={isActive ? 0 : -1}
                ref={(el) => {
                  optionRefs.current[index] = el;
                }}
                className={`popover-select__option org-wizard-role-select__option ${isSelected ? 'is-selected' : ''}`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                  triggerRef.current?.focus();
                }}
              >
                <div className="popover-select__option-text org-wizard-role-select__option-text">
                  <span className="popover-select__option-title org-wizard-role-select__option-title">
                    {option.title}
                  </span>
                  {option.description ? (
                    <span className="popover-select__option-description org-wizard-role-select__option-description">
                      {option.description}
                    </span>
                  ) : null}
                </div>
                {isSelected ? <i className="fa fa-check" aria-hidden="true" /> : null}
              </li>
            );
          })}
        </ul>
      ) : null}
    </div>
  );
};

// Generic typed export. Casting needed because TS doesn't preserve generics
// through React.FC; this is the standard idiom for generic React components.
const PopoverSelect = PopoverSelectInner as <T extends string | number>(
  props: PopoverSelectProps<T>
) => React.ReactElement;

export default PopoverSelect;
